// ============================================================================
// LA BARRE DE RÉGLAGES DES STATISTIQUES — PÉRIODE · FILTRES, lues de gauche à droite, partagées par les onglets d'un
// univers (Statistiques › Ventes). Réf. maquette du back-office : « BARRE DE RÉGLAGES DES STATS ».
//
// ⚠️ RIEN D'INACTIF À L'ÉCRAN : la période de comparaison n'existe que si l'on compare, un filtre n'apparaît que s'il
//    filtre. Les menus (dates, « Comparer à », « + Filtre ») s'ouvrent un à la fois, posés sous leur bouton.
// ⚠️ LA BARRE NE CALCULE RIEN : elle tient des choix (fenêtres, recherche, filtres) que le serveur applique.
// ============================================================================
import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, Trans, useTileDrag } from '@orgazone/ui-web';
import { sameWeekdayLastYear } from '@orgazone/core-analytics';
import type { IconName } from '@orgazone/ui-tokens';
import type { SalesSummaryFilterKey, SeasonPeriod } from '../api';
import { key, keyText, t, type Translatable } from '../i18n';
import { fieldLabel } from '../vocabulary';
import { currentLang } from '../lang';
import { shownValue } from './stats-format';
import type { VocabularyFieldKey } from '@orgazone/sync-contract';

// ─── Fenêtres de temps ───────────────────────────────────────────────────────────────────────────

/**
 * Une fenêtre : des dates (`AAAA-MM-JJ`) ou une saison datée — ses jours et les codes qu'elle réunit.
 * ⚠️ UNE SAISON PORTE SES DATES, telles que le serveur les sert (dates posées au référentiel, sinon celles du code —
 *    `sales/season-periods.ts`) : l'écran ne les refait pas, il les demande telles quelles.
 */
export type StatsWindow = { mode: 'dates'; from: string; to: string } | { mode: 'season'; from: string; to: string; codes: string[] };

const p2 = (n: number) => String(n).padStart(2, '0');
/** `AAAA-MM-JJ` d'une date locale — jamais `toISOString`, qui lit en UTC et décale d'un jour. */
export const isoOf = (d: Date): string => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
/** `AAAA-MM-JJ` → date locale. */
const dateOf = (iso: string): Date => { const [y, m, d] = iso.split('-').map(Number); return new Date(y!, m! - 1, d!); };
const addDays = (iso: string, n: number): string => { const d = dateOf(iso); d.setDate(d.getDate() + n); return isoOf(d); };
/** Le nombre de jours d'une fenêtre, bornes incluses. */
const spanDays = (from: string, to: string): number => Math.round((dateOf(to).getTime() - dateOf(from).getTime()) / 86_400_000) + 1;

/** `AAAAMMJJ` → `AAAA-MM-JJ`. */
const isoOfDay = (ymd: string): string => `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6)}`;

/** La fenêtre d'une saison servie. */
export const seasonWindow = (p: SeasonPeriod): StatsWindow => ({ mode: 'season', from: isoOfDay(p.from), to: isoOfDay(p.to), codes: [...p.codes] });

/** Les dates d'une fenêtre. */
export function rangeOf(w: StatsWindow): { from: string; to: string } {
  return { from: w.from, to: w.to };
}

/**
 * Le nom court d'une saison : « AH26 » / « PE26 » pour une demi-saison (dans la langue du compte), ses codes sinon.
 * ⚠️ UNE SAISON RÉUNIT PLUSIEURS CODES (« HW2026 » et « 092026 ») : son nom est celui de ses DATES, jamais l'un de ses codes.
 * Les jours se lisent en `AAAAMMJJ` comme en `AAAA-MM-JJ`.
 */
export function seasonName(from: string, to: string, codes: readonly string[]): string {
  const f = from.replace(/-/g, '');
  const e = to.replace(/-/g, '');
  const yy = f.slice(2, 4);
  if (f.slice(4) === '0901' && e.slice(0, 6) === `${Number(f.slice(0, 4)) + 1}02`) return t('AH{aa}', { aa: yy });
  if (f.slice(4) === '0301' && e === `${f.slice(0, 4)}0831`) return t('PE{aa}', { aa: yy });
  return codes.map(shownValue).join(' · ');
}

/** « 01/09 → 20/09/2026 » — l'année n'est écrite qu'une fois quand les deux bornes la partagent. */
export function rangeLabel(from: string, to: string): string {
  const fr = (iso: string) => { const [y, m, d] = iso.split('-'); return y && m && d ? `${d}/${m}/${y}` : '—'; };
  if (!from || !to) return '—';
  if (from === to) return fr(from);
  return `${from.slice(0, 4) === to.slice(0, 4) ? fr(from).slice(0, 5) : fr(from)} → ${fr(to)}`;
}

/** Le libellé d'une fenêtre : sa saison ou ses dates. */
export const windowLabel = (w: StatsWindow): string =>
  w.mode === 'season' ? t('Saison {code}', { code: seasonName(w.from, w.to, w.codes) }) : rangeLabel(w.from, w.to);

/**
 * La même saison un an plus tôt, parmi celles du catalogue — trouvée par ses DATES, jamais fabriquée : une saison qui
 * n'existe pas au catalogue n'a aucune vente à comparer.
 */
const seasonBefore = (from: string, seasons: readonly SeasonPeriod[]): SeasonPeriod | null => {
  const back = `${Number(from.slice(0, 4)) - 1}${from.slice(4)}`;
  return seasons.find((p) => isoOfDay(p.from) === back) ?? null;
};
/** La saison juste avant, parmi celles du catalogue : celle qui finit la veille du premier jour de celle-ci. */
const seasonPrevious = (from: string, seasons: readonly SeasonPeriod[]): SeasonPeriod | null =>
  seasons.find((p) => isoOfDay(p.to) === addDays(from, -1)) ?? null;

// ─── État partagé par les onglets ────────────────────────────────────────────────────────────────

/** Ce que la barre tient : la période analysée, la comparaison choisie (`null` = N-1), la recherche et les filtres. */
export interface StatsSettings {
  a: StatsWindow;
  b: StatsWindow | null;
  q: string;
  filters: Partial<Record<SalesSummaryFilterKey, string[]>>;
}

/** Par défaut : le mois en cours jusqu'à aujourd'hui, comparé à N-1. */
export function defaultSettings(today: Date): StatsSettings {
  const iso = isoOf(today);
  return { a: { mode: 'dates', from: `${iso.slice(0, 8)}01`, to: iso }, b: null, q: '', filters: {} };
}

/**
 * Les sept critères de filtre — le champ qui les nomme et leur pictogramme (maquette `STDIMS`).
 * ⚠️ LE NOM D'UN CHAMP EST LE MOT DU MAGASIN (`fieldLabel`, lu au RENDU) : un caviste lit « Millésime » là où le dessin
 *    dit « Saison ». Seul « Article » n'est pas un champ de la fiche, et se traduit comme nos textes.
 */
export const FILTER_DIMS: { k: SalesSummaryFilterKey; field?: VocabularyFieldKey; l?: Translatable; ic: IconName }[] = [
  { k: 'sup', field: 'supplier', ic: 'truck' },
  { k: 'marq', field: 'brand', ic: 'tag' },
  { k: 'sect', field: 'sector', ic: 'grid' },
  { k: 'fam', field: 'family', ic: 'folder' },
  { k: 'sfam', field: 'subFamily', ic: 'subfolder' },
  { k: 'sais', field: 'season', ic: 'sun' },
  { k: 'art', l: key('Article'), ic: 'tshirt' },
];

/** Un niveau de regroupement : une clé, le champ qui le nomme (ou notre texte), son pictogramme. */
export interface LevelDim { k: string; field?: VocabularyFieldKey; l?: Translatable; ic: IconName }

/**
 * Les niveaux du reporting articles : les sept critères, plus le COLORIS et le GENCODE (une ligne par déclinaison).
 * ⚠️ Ils ne sont pas des filtres : un filtre par coloris porterait des centaines de valeurs pour un tableau qui les
 *    montre déjà en lignes.
 */
export const ARTICLE_LEVEL_DIMS: LevelDim[] = [
  ...FILTER_DIMS,
  { k: 'coul', field: 'color', ic: 'dot' },
  { k: 'ean', field: 'gencode', ic: 'barcode' },
];

/** Le nom d'un critère de filtre ou d'un niveau, dans le mot du magasin. */
export const dimLabel = (d: LevelDim): string => (d.field ? fieldLabel(d.field) : d.l ? t(d.l) : '');

// ─── Les styles de la maquette ───────────────────────────────────────────────────────────────────

const LABEL: CSSProperties = {
  width: 58, flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
  textTransform: 'uppercase', color: 'var(--ink-500)',
};
/** Le nom d'une fenêtre devant son calendrier (« Ventes », « Réceptions »). */
const WINDOW_NAME: CSSProperties = { fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-600)', whiteSpace: 'nowrap' };
const ROW: CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px 10px', flexWrap: 'wrap', padding: '9px 13px' };
const DASHED: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', border: '1.5px dashed var(--ink-300)',
  borderRadius: 'var(--r-pill)', background: 'transparent', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700,
  whiteSpace: 'nowrap', cursor: 'pointer',
};
export const POP_HEAD: CSSProperties = {
  padding: '11px 13px 4px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
  textTransform: 'uppercase', color: 'var(--ink-500)',
};
/** Une pastille de choix d'un menu : cochée ou non. */
const chip = (on: boolean): CSSProperties => ({
  border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`,
  background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-700)',
});

/**
 * LA POSITION D'UN MENU : sous son bouton, ou au-dessus s'il manque la place ; jamais hors de la fenêtre.
 * ⚠️ EN POSITION FIXE, posé à la racine : dans la carte, un menu serait coupé par son bord.
 */
function popStyle(r: DOMRect, width: number): CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.round(Math.min(Math.max(8, r.left), vw - width - 8));
  const below = vh - r.bottom - 20;
  const above = r.top - 20;
  const up = below < 420 && above > below;
  return {
    position: 'fixed', ...(up ? { bottom: Math.round(vh - r.top + 6) } : { top: Math.round(r.bottom + 6) }), left, zIndex: 600, width,
    maxWidth: 'calc(100vw - 16px)', maxHeight: Math.round(Math.max(220, up ? above : below)), overflowY: 'auto', overflowX: 'hidden',
    background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: '0 18px 44px -12px rgba(12,14,16,.28)',
  };
}

type Menu =
  | { kind: 'dates'; w: 'a' | 'b' | 'r'; r: DOMRect }
  | { kind: 'compare'; r: DOMRect }
  | { kind: 'filters'; r: DOMRect }
  | { kind: 'criterion'; k: SalesSummaryFilterKey; r: DOMRect };

/**
 * Un menu posé à la racine, avec son voile qui le ferme au clic dehors.
 * ⚠️ EXPORTÉ (`StatsPopover`) pour les réglages qu'un onglet pose au bout de la ligne PÉRIODE : un second menu écrit à
 *    côté se placerait autrement.
 */
export function StatsPopover({ r, width, onClose, children }: { r: DOMRect; width: number; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 590 }} />
      <div role="dialog" style={popStyle(r, width)}>{children}</div>
    </>
  );
}

// ─── La barre ────────────────────────────────────────────────────────────────────────────────────

/**
 * LA BARRE — période et comparaison, puis recherche et filtres. `scope` est le compte affiché à droite des filtres
 * (« 30 articles · 6 lignes »), `options` les valeurs offertes par chaque critère, `seasons` les saisons datées.
 *
 * Ce qui change d'un onglet à l'autre :
 * - `compare` — `'n1'` (Résumé) : sans choix, les écarts se lisent face à N-1 (même jour de semaine), et la barre le dit ; `'chosen'`
 *   (Reporting) : sans choix il n'y a AUCUNE comparaison, et la retirer l'arrête ; `'none'` (Stock par périodes) : l'onglet ne
 *   compare pas, la barre n'en offre aucune — une comparaison choisie ailleurs n'y est ni montrée ni envoyée ;
 * - `periodEnd` / `filtersEnd` — ce que l'onglet pose au bout des deux lignes (« Afficher », « Colonnes ») ;
 * - `children` — les lignes de réglage propres à l'onglet (LIGNES), dans la même carte ;
 * - `framed` — `false` quand la carte est celle de l'onglet, qui y pose son tableau.
 */
export function StatsBar({
  value, onChange, today, scope, options, seasons, compare = 'n1', periodEnd, filtersEnd, children, framed = true, periodNote, receptions, filters = true,
}: {
  value: StatsSettings;
  onChange: (next: StatsSettings) => void;
  today: string;
  scope: string;
  options: Partial<Record<SalesSummaryFilterKey, { value: string; label: string }[]>>;
  seasons: SeasonPeriod[];
  compare?: 'n1' | 'chosen' | 'none';
  periodEnd?: ReactNode;
  filtersEnd?: ReactNode;
  children?: ReactNode;
  framed?: boolean;
  /** Ce que le serveur a fait de la période (« à date »), dit à côté d'elle. */
  periodNote?: string;
  /**
   * LA FENÊTRE RÉCEPTIONS (Reporting articles) : la période devient « Ventes », une seconde fenêtre la suit, et il n'y a
   * plus de comparaison — deux fenêtres de nature différente ne se comparent pas. `help` dit ce qui suit laquelle.
   */
  receptions?: { value: StatsWindow; onChange: (w: StatsWindow) => void; help: string };
  /** `false` : un onglet dont les chiffres ne se filtrent pas par article — la ligne des filtres n'est pas offerte. */
  filters?: boolean;
}) {
  const [menu, setMenu] = useState<Menu | null>(null);
  const close = () => setMenu(null);
  const open = (m: Menu) => setMenu((cur) => (cur && cur.kind === m.kind && (cur.kind !== 'dates' || (m.kind === 'dates' && cur.w === m.w)) ? null : m));
  const rectOf = (e: React.MouseEvent) => (e.currentTarget as HTMLElement).getBoundingClientRect();
  const filtersOn = !!value.q || Object.values(value.filters).some((v) => v && v.length);
  const chips = FILTER_DIMS.filter((d) => (value.filters[d.k] ?? []).length);

  return (
    <div style={framed ? { background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', marginBottom: 13 } : undefined}>
      <div style={filters || children ? { ...ROW, borderBottom: '1px solid var(--ink-100)' } : ROW}>
        <span style={LABEL}>{t('Période')}</span>
        {receptions && <span style={WINDOW_NAME}>{t('Ventes')}</span>}
        <button type="button" onClick={(e) => open({ kind: 'dates', w: 'a', r: rectOf(e) })}
          className="oz-stats-period"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, height: 34, padding: '0 11px', border: '1px solid var(--ink-200)',
            borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-body)', fontWeight: 700,
            whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', cursor: 'pointer',
          }}>
          <Icon name="calendar" size={14} stroke={1.9} color="var(--teal-700)" style={{ flexShrink: 0 }} />
          {windowLabel(value.a)}
          <Icon name="chevronD" size={12} stroke={2.2} style={{ flexShrink: 0, opacity: 0.55 }} />
        </button>
        {receptions ? (
          <>
            <span style={WINDOW_NAME}>{t('Réceptions')}</span>
            <button type="button" onClick={(e) => open({ kind: 'dates', w: 'r', r: rectOf(e) })} className="oz-stats-period" data-window="receptions"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, height: 34, padding: '0 11px', border: '1px solid var(--ink-200)',
                borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-body)', fontWeight: 700,
                whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', cursor: 'pointer',
              }}>
              <Icon name="calendar" size={14} stroke={1.9} color="var(--blue-fg)" style={{ flexShrink: 0 }} />
              {windowLabel(receptions.value)}
              <Icon name="chevronD" size={12} stroke={2.2} style={{ flexShrink: 0, opacity: 0.55 }} />
            </button>
            <span role="note" aria-label={receptions.help} title={receptions.help} style={{
              width: 22, height: 22, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-500)', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'help',
            }}>
              <Icon name="info" size={12} stroke={2.4} />
            </span>
          </>
        ) : compare === 'none' ? null : value.b === null ? (
          <>
            <button type="button" onClick={(e) => open({ kind: 'compare', r: rectOf(e) })} className="oz-stats-dashed" style={DASHED}
              title={t('Comparer à une autre période ou une autre saison')}>
              <Icon name="compare" size={13} stroke={2.1} />{t('Comparer à…')}
            </button>
            {compare === 'n1' && (
              <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Sans choix, les écarts se lisent face à N-1, même jour de semaine.')}</span>
            )}
          </>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 34, borderRadius: 'var(--r-ctrl)', background: 'var(--blue-bg)', color: 'var(--blue-fg)' }}>
            <button type="button" onClick={(e) => open({ kind: 'dates', w: 'b', r: rectOf(e) })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, height: '100%', padding: '0 6px 0 11px', border: 'none',
                background: 'transparent', color: 'inherit', fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums', cursor: 'pointer',
              }}>
              <Trans k='<1>comparé à</1> {periode}' vars={{ periode: windowLabel(value.b) }}
                parts={[(x) => <span style={{ fontWeight: 600 }}>{x}</span>]} />
              <Icon name="chevronD" size={12} stroke={2.2} style={{ flexShrink: 0, opacity: 0.55 }} />
            </button>
            <button type="button" onClick={() => onChange({ ...value, b: null })} aria-label={t('Arrêter la comparaison')}
              title={compare === 'n1' ? t('Revenir à la comparaison N-1') : t('Arrêter la comparaison')} className="oz-stats-chip-x"
              style={{
                width: 28, height: '100%', border: 'none', borderRadius: '0 var(--r-ctrl) var(--r-ctrl) 0', background: 'transparent',
                color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}>
              <Icon name="x" size={11} stroke={2.6} />
            </button>
          </span>
        )}
        {periodNote && <span role="note" style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{periodNote}</span>}
        {periodEnd && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>{periodEnd}</span>}
      </div>
      {filters && <div style={children ? { ...ROW, borderBottom: '1px solid var(--ink-100)' } : ROW}>
        <span style={LABEL}>{t('Filtres')}</span>
        <div style={{
          flex: '0 1 240px', minWidth: 160, display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: '0 11px',
          border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
        }}>
          <Icon name="search" size={14} stroke={2} color="var(--ink-400)" style={{ flexShrink: 0 }} />
          <input value={value.q} onChange={(e) => onChange({ ...value, q: e.target.value })}
            placeholder={t('Rechercher un article, un code…')} aria-label={t('Rechercher un article, un code…')}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: 'var(--t-body)', color: 'var(--ink-900)', outline: 'none' }} />
        </div>
        {chips.map((d) => {
          const v = value.filters[d.k] ?? [];
          const shown = v.slice(0, 2).map((x) => shownValue(options[d.k]?.find((o) => o.value === x)?.label ?? x)).join(', ') + (v.length > 2 ? ` +${v.length - 2}` : '');
          return (
            <span key={d.k} style={{
              display: 'inline-flex', alignItems: 'center', height: 32, borderRadius: 'var(--r-pill)', background: 'var(--teal-100)',
              border: '1px solid var(--teal-400)', color: 'var(--teal-800)', maxWidth: 320,
            }}>
              <button type="button" onClick={(e) => open({ kind: 'criterion', k: d.k, r: rectOf(e) })} title={t('Modifier ce filtre')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, height: '100%', minWidth: 0, padding: '0 5px 0 12px', border: 'none',
                  background: 'transparent', color: 'inherit', fontSize: 'var(--t-dense)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
                }}>
                <Trans k='<1>{critere} :</1> <2>{valeurs}</2>' vars={{ critere: dimLabel(d), valeurs: shown }} parts={[
                  (x) => <span style={{ fontWeight: 600, opacity: 0.8, flexShrink: 0 }}>{x}</span>,
                  (x) => <strong style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{x}</strong>,
                ]} />
              </button>
              <button type="button" onClick={() => onChange({ ...value, filters: { ...value.filters, [d.k]: [] } })}
                aria-label={t('Retirer le filtre {critere}', { critere: dimLabel(d) })} title={t('Retirer ce filtre')} className="oz-stats-chip-x"
                style={{
                  width: 26, height: '100%', flexShrink: 0, border: 'none', borderRadius: '0 var(--r-pill) var(--r-pill) 0',
                  background: 'transparent', color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}>
                <Icon name="x" size={11} stroke={2.6} />
              </button>
            </span>
          );
        })}
        <button type="button" onClick={(e) => open({ kind: 'filters', r: rectOf(e) })} className="oz-stats-dashed" style={DASHED}>
          <Icon name="plus" size={12} stroke={2.6} />{t('Filtre')}
        </button>
        {filtersOn && (
          <button type="button" onClick={() => onChange({ ...value, q: '', filters: {} })} className="oz-stats-reset"
            style={{
              height: 32, padding: '0 10px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-500)',
              fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
            }}>
            <Icon name="x" size={11} stroke={2.6} />{t('Tout effacer')}
          </button>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{scope}</span>
          {filtersEnd}
        </span>
      </div>}
      {children}

      {menu?.kind === 'dates' && (
        <DatesMenu r={menu.r} today={today} seasons={seasons} onClose={close}
          title={menu.w === 'a' ? (receptions ? t('Fenêtre des ventes') : t('Période analysée')) : menu.w === 'r' ? t('Fenêtre des réceptions') : t('Période de comparaison')}
          current={menu.w === 'a' ? value.a : menu.w === 'r' ? receptions?.value ?? null : value.b}
          onSet={(w) => { if (menu.w === 'r') receptions?.onChange(w); else onChange(menu.w === 'a' ? { ...value, a: w } : { ...value, b: w }); }} />
      )}
      {menu?.kind === 'compare' && (
        <CompareMenu r={menu.r} value={value} seasons={seasons}
          onPick={(b, thenDates) => { onChange({ ...value, b }); setMenu(thenDates ? { kind: 'dates', w: 'b', r: menu.r } : null); }}
          onClose={close} />
      )}
      {menu?.kind === 'filters' && (
        <StatsPopover r={menu.r} width={250} onClose={close}>
          <div style={POP_HEAD}>{t('Filtrer par')}</div>
          <div style={{ padding: '4px 6px 6px' }}>
            {FILTER_DIMS.map((d) => {
              const n = (value.filters[d.k] ?? []).length;
              return (
                <button key={d.k} type="button" onClick={() => setMenu({ kind: 'criterion', k: d.k, r: menu.r })} className="oz-stats-menu-row"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 36, padding: '0 9px', border: 'none',
                    borderRadius: 'var(--r-xs)', background: 'transparent', textAlign: 'left', cursor: 'pointer',
                  }}>
                  <Icon name={d.ic} size={15} stroke={1.8} color="var(--teal-700)" style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)' }}>{dimLabel(d)}</span>
                  {n > 0 && (
                    <span style={{
                      minWidth: 20, height: 20, padding: '0 6px', borderRadius: 'var(--r-pill)', background: 'var(--teal-500)', color: '#fff',
                      fontSize: 'var(--t-meta)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
                    }}>{n}</span>
                  )}
                  <Icon name="chevronR" size={12} stroke={2.2} color="var(--ink-400)" style={{ flexShrink: 0 }} />
                </button>
              );
            })}
          </div>
        </StatsPopover>
      )}
      {menu?.kind === 'criterion' && (
        <CriterionMenu r={menu.r} dim={menu.k} options={options[menu.k] ?? []} selected={value.filters[menu.k] ?? []}
          onChange={(sel) => onChange({ ...value, filters: { ...value.filters, [menu.k]: sel } })} onClose={close} />
      )}
    </div>
  );
}

/**
 * LES RACCOURCIS DU SUIVI DES OBJECTIFS — des mois entiers, jusqu'à leur dernier jour : un objectif court jusqu'au bout de
 * sa période, et ses jours à venir portent une cible.
 */
function objectivePresets(today: string): [Translatable, string, string][] {
  const d = dateOf(today);
  const monthOf = (offset: number): [string, string] => [
    isoOf(new Date(d.getFullYear(), d.getMonth() + offset, 1)), isoOf(new Date(d.getFullYear(), d.getMonth() + offset + 1, 0)),
  ];
  const monday = addDays(today, -((d.getDay() + 6) % 7));
  return [
    [key('Ce mois-ci'), ...monthOf(0)],
    [key('Mois dernier'), ...monthOf(-1)],
    [key('Mois prochain'), ...monthOf(1)],
    [key('Cette semaine'), monday, addDays(monday, 6)],
    [key('7 derniers jours'), addDays(today, -6), today],
    [key('30 derniers jours'), addDays(today, -29), today],
  ];
}

/**
 * Le menu des dates d'une fenêtre : raccourcis, saisons datées, et « Du / Au ».
 * `future` : la fenêtre peut courir au-delà d'aujourd'hui (le suivi des objectifs) — ses raccourcis sont alors des mois
 * entiers, et les dates ne s'arrêtent pas à aujourd'hui.
 */
function DatesMenu({ r, title, current, onSet, today, seasons, onClose, future = false }: {
  r: DOMRect; title: string; current: StatsWindow | null; onSet: (w: StatsWindow) => void; today: string; seasons: SeasonPeriod[]; onClose: () => void;
  future?: boolean;
}) {
  const cur = current;
  const range = cur ? rangeOf(cur) : { from: '', to: '' };
  const set = onSet;
  const y = Number(today.slice(0, 4));
  const ym = today.slice(0, 7);
  const prevMonthEnd = (() => { const d = dateOf(`${ym}-01`); d.setDate(0); return d; })();
  const quick: [Translatable, string, string][] = future ? objectivePresets(today) : [
    [key('Hier'), addDays(today, -1), addDays(today, -1)],
    [key('7 derniers jours'), addDays(today, -6), today],
    [key('30 derniers jours'), addDays(today, -29), today],
    [key('Ce mois-ci'), `${ym}-01`, today],
    [key('Mois dernier'), isoOf(new Date(prevMonthEnd.getFullYear(), prevMonthEnd.getMonth(), 1)), isoOf(prevMonthEnd)],
    [key('12 derniers mois'), addDays(isoOf(new Date(y - 1, Number(today.slice(5, 7)) - 1, Number(today.slice(8, 10)))), 1), today],
    [key('Cette année'), `${y}-01-01`, today],
    [key('Année dernière'), `${y - 1}-01-01`, `${y - 1}-12-31`],
  ];
  const inSeason = cur?.mode === 'season' ? `${cur.from}-${cur.to}` : '';
  const days = range.from && range.to ? spanDays(range.from, range.to) : 0;
  const hint = cur?.mode === 'season' ? windowLabel(cur)
    : days ? t('{n} jour · {plage}', { n: days, plage: rangeLabel(range.from, range.to) }) : '';
  const clamp = (v: string) => (!future && v > today ? today : v);
  const dateInput: CSSProperties = {
    width: '100%', height: 36, padding: '0 8px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
    color: 'var(--ink-900)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', outline: 'none', boxSizing: 'border-box',
  };
  return (
    <StatsPopover r={r} width={330} onClose={onClose}>
      <div style={POP_HEAD}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, padding: '6px 10px 10px' }}>
        {quick.map(([l, f, to]) => (
          <button key={keyText(l)} type="button" onClick={() => { set({ mode: 'dates', from: f, to }); onClose(); }}
            style={{
              height: 32, padding: '0 10px', borderRadius: 'var(--r-ctrl)', ...chip(!inSeason && range.from === f && range.to === to),
              fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{t(l)}</button>
        ))}
      </div>
      {seasons.length > 0 && (
        <>
          <div style={{ ...POP_HEAD, borderTop: '1px solid var(--ink-100)', paddingTop: 10 }}>{t('Saisons')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 5, padding: '6px 10px 10px' }}>
            {seasons.map((p) => {
              const w = seasonWindow(p);
              return (
                <button key={`${p.from}-${p.to}`} type="button" onClick={() => { set(w); onClose(); }} title={p.codes.map(shownValue).join(' · ')}
                  style={{
                    height: 32, padding: '0 8px', borderRadius: 'var(--r-ctrl)', ...chip(inSeason === `${w.from}-${w.to}`), fontSize: 'var(--t-dense)',
                    fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{seasonName(p.from, p.to, p.codes)}</button>
              );
            })}
          </div>
        </>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', padding: '11px 10px', borderTop: '1px solid var(--ink-100)' }}>
        <label style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-sub)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 4 }}>{t('Du')}</span>
          <input type="date" value={inSeason ? '' : range.from} max={future ? undefined : today} style={dateInput}
            onChange={(e) => { const v = clamp(e.target.value); if (!v) return; set({ mode: 'dates', from: v, to: range.to && range.to >= v ? range.to : v }); }} />
        </label>
        <label style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-sub)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 4 }}>{t('Au')}</span>
          <input type="date" value={inSeason ? '' : range.to} max={future ? undefined : today} style={dateInput}
            onChange={(e) => { const v = clamp(e.target.value); if (!v) return; set({ mode: 'dates', from: range.from && range.from <= v ? range.from : v, to: v }); }} />
        </label>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hint}</span>
        <button type="button" onClick={onClose} className="oz-stats-close"
          style={{ height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer' }}>
          {t('Fermer')}
        </button>
      </div>
    </StatsPopover>
  );
}

/**
 * LA BARRE D'UNE SEULE FENÊTRE — la période du suivi des objectifs : ni comparaison, ni filtre (un objectif est celui du
 * magasin entier). `note` dit ce que le serveur a fait de la période (ramenée à deux mois).
 */
export function StatsPeriodCard({ value, onChange, today, note }: {
  value: StatsWindow; onChange: (w: StatsWindow) => void; today: string; note?: string;
}) {
  const [menu, setMenu] = useState<DOMRect | null>(null);
  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden', marginBottom: 13 }}>
      <div style={ROW}>
        <span style={LABEL}>{t('Période')}</span>
        <button type="button" onClick={(e) => { const rect = (e.currentTarget as HTMLElement).getBoundingClientRect(); setMenu((cur) => (cur ? null : rect)); }}
          className="oz-stats-period"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, height: 34, padding: '0 11px', border: '1px solid var(--ink-200)',
            borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-body)', fontWeight: 700,
            whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', cursor: 'pointer',
          }}>
          <Icon name="calendar" size={14} stroke={1.9} color="var(--teal-700)" style={{ flexShrink: 0 }} />
          {windowLabel(value)}
          <Icon name="chevronD" size={12} stroke={2.2} style={{ flexShrink: 0, opacity: 0.55 }} />
        </button>
        {note && <span role="note" style={{ fontSize: 'var(--t-note)', color: 'var(--amber-fg)' }}>{note}</span>}
      </div>
      {menu && (
        <DatesMenu r={menu} title={t('Période')} current={value} onSet={onChange} today={today} seasons={[]} future onClose={() => setMenu(null)} />
      )}
    </div>
  );
}

/** Le menu « Comparer à » : période précédente, même période l'an dernier, une autre saison, des dates choisies. */
function CompareMenu({ r, value, seasons, onPick, onClose }: {
  r: DOMRect; value: StatsSettings; seasons: SeasonPeriod[]; onPick: (b: StatsWindow, thenDates: boolean) => void; onClose: () => void;
}) {
  const a = value.a;
  const prevSeason = a.mode === 'season' ? seasonPrevious(a.from, seasons) : null;
  const beforeSeason = a.mode === 'season' ? seasonBefore(a.from, seasons) : null;
  const prev: StatsWindow | null = a.mode === 'season'
    ? (prevSeason ? seasonWindow(prevSeason) : null)
    : (() => { const n = spanDays(a.from, a.to); const to = addDays(a.from, -1); return { mode: 'dates' as const, from: addDays(to, -(n - 1)), to }; })();
  const n1: StatsWindow | null = a.mode === 'season'
    ? (beforeSeason ? seasonWindow(beforeSeason) : null)
    : (() => {
      // ⚠️ LA RÈGLE DU SERVEUR, PAS UNE COPIE (`sameWeekdayLastYear`, 364 jours) : l'écran doit demander la période que
      //    le serveur prend sans choix — les mêmes jours de semaine, jamais la même date.
      const back = (iso: string) => {
        const y = sameWeekdayLastYear(iso.replace(/-/g, ''));
        return `${y.slice(0, 4)}-${y.slice(4, 6)}-${y.slice(6, 8)}`;
      };
      return { mode: 'dates' as const, from: back(a.from), to: back(a.to) };
    })();
  const rows: { id: string; l: string; sub: string; ic: IconName; pick: (() => void) | null }[] = [
    {
      id: 'prev', l: t('Période précédente'), ic: 'chevronsL',
      sub: prev ? windowLabel(prev) : a.mode === 'season' ? t('Aucune saison précédente') : '—',
      pick: prev ? () => onPick(prev, false) : null,
    },
    {
      id: 'n1', l: t('N-1, même jour de semaine'), ic: 'history',
      sub: n1 ? windowLabel(n1) : a.mode === 'season' ? t('Aucune saison un an plus tôt') : '—',
      pick: n1 ? () => onPick(n1, false) : null,
    },
    {
      id: 'sais', l: t('Une autre saison'), ic: 'layers', sub: t('Choisir la saison à comparer'),
      pick: seasons.length ? () => onPick(seasonWindow(seasons[0]!), true) : null,
    },
    {
      id: 'dates', l: t('Des dates personnalisées'), ic: 'calendar', sub: t('Du… au…'),
      pick: () => onPick(prev ?? a, true),
    },
  ];
  return (
    <StatsPopover r={r} width={300} onClose={onClose}>
      <div style={POP_HEAD}>{t('Comparer à')}</div>
      <div style={{ padding: '4px 6px 6px' }}>
        {rows.map((o) => (
          <button key={o.id} type="button" onClick={o.pick ?? undefined} disabled={!o.pick} className="oz-stats-menu-row"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 9px', border: 'none', borderRadius: 'var(--r-xs)',
              background: 'transparent', textAlign: 'left', opacity: o.pick ? 1 : 0.45, cursor: o.pick ? 'pointer' : 'default',
            }}>
            <span style={{
              width: 28, height: 28, borderRadius: 'var(--r-xs)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><Icon name={o.ic} size={14} stroke={2} /></span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{o.l}</span>
              <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' }}>{o.sub}</span>
            </span>
          </button>
        ))}
      </div>
    </StatsPopover>
  );
}

/** Le choix des valeurs d'un critère : recherche au-delà de huit valeurs, cases à cocher. */
function CriterionMenu({ r, dim, options, selected, onChange, onClose }: {
  r: DOMRect; dim: SalesSummaryFilterKey; options: { value: string; label: string }[]; selected: string[];
  onChange: (sel: string[]) => void; onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement | null>(null);
  useEffect(() => { input.current?.focus(); }, []);
  const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  // ⚠️ DANS L'ORDRE DE LA LANGUE DE QUI REGARDE : le serveur trie pour être stable, l'écran pour être lu (« (non renseigné) »
  //    et les accents ne se rangent pas pareil en français et en anglais).
  const shown = useMemo(() => options
    .filter((o) => !q || fold(shownValue(o.label)).includes(fold(q)))
    .sort((x, y) => shownValue(x.label).localeCompare(shownValue(y.label), currentLang())), [options, q]);
  const def = FILTER_DIMS.find((d) => d.k === dim);
  return (
    <StatsPopover r={r} width={280} onClose={onClose}>
      {options.length > 8 && (
        <div style={{ padding: 8, borderBottom: '1px solid var(--ink-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', borderRadius: 'var(--r-xs)', background: 'var(--ink-50)', color: 'var(--ink-400)' }}>
            <Icon name="search" size={14} stroke={2} style={{ flexShrink: 0 }} />
            <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Rechercher parmi {n}…', { n: options.length })}
              aria-label={def ? dimLabel(def) : undefined}
              style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontSize: 'var(--t-lead)', color: 'var(--ink-900)' }} />
          </div>
        </div>
      )}
      <div style={{ padding: 5 }}>
        {shown.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button key={o.value} type="button" role="checkbox" aria-checked={on} className="oz-stats-pick-row"
              onClick={() => onChange(on ? selected.filter((v) => v !== o.value) : [...selected, o.value])}
              style={{
                display: 'block', width: '100%', height: 32, padding: '0 10px', border: 'none', borderRadius: 'var(--r-xs)',
                background: on ? 'var(--teal-100)' : 'transparent', color: on ? 'var(--teal-800)' : 'var(--ink-800)', fontSize: 'var(--t-lead)',
                fontWeight: on ? 600 : 500, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer',
              }}>
              <span style={{
                width: 16, height: 16, borderRadius: 'var(--r-xs)', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`,
                background: on ? 'var(--teal-500)' : 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, boxSizing: 'border-box', marginRight: 8, verticalAlign: -3,
              }}>{on && <Icon name="check" size={10} stroke={3.4} color="#fff" />}</span>
              {shownValue(o.label)}
            </button>
          );
        })}
        {!shown.length && (
          <div style={{ padding: '14px 10px', fontSize: 'var(--t-body)', color: 'var(--ink-400)', textAlign: 'center' }}>{t('Aucun résultat')}</div>
        )}
      </div>
    </StatsPopover>
  );
}

// ─── La ligne LIGNES ─────────────────────────────────────────────────────────────────────────────

/** Une pastille de niveau — la maquette (« LIGNES », Reporting ventes). */
const LEVEL: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 7, height: 32, padding: '0 6px 0 9px', border: '1.5px solid var(--teal-400)',
  borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700,
  whiteSpace: 'nowrap', cursor: 'grab', touchAction: 'none', userSelect: 'none',
};

/** La pastille d'un niveau qui ne se change pas — la maquette (« LIGNES », Vendeurs reporting). */
const FIXED_LEVEL: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 7, height: 32, padding: '0 12px', border: '1.5px solid var(--teal-400)',
  borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-dense)', fontWeight: 700,
  whiteSpace: 'nowrap',
};

/**
 * LIGNES, QUAND LA VENTILATION NE SE CHOISIT PAS (le Reporting vendeurs : toujours le vendeur) — une pastille fixe, qui le
 * dit au survol, et la légende des blocs affichés.
 */
export function StatsFixedLinesRow({ label, icon, hint, legend }: {
  label: string;
  icon: IconName;
  hint: string;
  legend: { label: string; color: string }[];
}) {
  return (
    <div className="oz-stats-lines" style={{ ...ROW, borderBottom: '1px solid var(--ink-200)' }}>
      <span style={LABEL}>{t('Lignes')}</span>
      <span title={hint} style={FIXED_LEVEL}>
        <Icon name={icon} size={13} stroke={2} />{label}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginLeft: 'auto' }}>
        {legend.map((x) => (
          <span key={x.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-600)' }}>
            <span style={{ width: 16, height: 4, borderRadius: 2, background: x.color }} />{x.label}
          </span>
        ))}
      </span>
    </div>
  );
}

/**
 * LIGNES — les niveaux de regroupement d'un tableau, du plus large au plus fin : on les glisse pour changer l'ordre, on
 * en retire (il en reste toujours un), on en ajoute ; à droite, la légende des blocs de colonnes affichés.
 * ⚠️ LE NOM D'UN NIVEAU EST LE MOT DU MAGASIN, comme celui du filtre du même critère.
 */
export function StatsLinesRow<K extends string = SalesSummaryFilterKey>({ axes, onChange, legend, dims = FILTER_DIMS }: {
  axes: K[];
  onChange: (next: K[]) => void;
  legend: { label: string; color: string }[];
  /** Les niveaux offerts — les sept critères par défaut. */
  dims?: readonly LevelDim[];
}) {
  const [menu, setMenu] = useState<DOMRect | null>(null);
  const drag = useTileDrag({
    keys: axes, axis: 'xy', inPlace: true, ghostScope: 'oz-stats-lines', ignoreSelector: 'button',
    onReorder: (next) => onChange(next as K[]),
  });
  const free = dims.filter((d) => !axes.includes(d.k as K));
  return (
    <div className="oz-stats-lines" style={{ ...ROW, borderBottom: '1px solid var(--ink-200)' }}>
      <span style={LABEL}>{t('Lignes')}</span>
      {drag.order.map((k, i) => {
        const d = dims.find((x) => x.k === k);
        if (!d) return null;
        const tile = drag.tile(k);
        const handle = drag.handle(k);
        return (
          <Fragment key={k}>
            <span ref={tile.ref} data-tile-key={tile['data-tile-key']} onPointerDown={handle.onPointerDown}
              tabIndex={0} onKeyDown={(e) => {
                // ⚠️ LE CLAVIER DÉPLACE AUSSI : un réglage qu'on ne peut changer qu'au pointeur exclut qui ne s'en sert pas.
                const delta = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
                const to = i + delta;
                if (!delta || to < 0 || to >= axes.length) return;
                e.preventDefault();
                const next = [...axes];
                next.splice(i, 1);
                next.splice(to, 0, k as K);
                onChange(next);
              }}
              title={t('Glisser, ou flèches gauche et droite, pour changer l’ordre des niveaux')}
              style={{ ...LEVEL, ...tile.style, ...handle.style, cursor: 'grab' }}>
              {/* La poignée de la charte, ramenée aux 8 px de large du dessin : ses points n'occupent que le milieu de sa case. */}
              <Icon name="drag" size={13} stroke={2} style={{ opacity: 0.45, flexShrink: 0, margin: '0 -2.5px' }} />
              {dimLabel(d)}
              {axes.length > 1 && (
                <button type="button" onClick={() => onChange(axes.filter((x) => x !== k))}
                  aria-label={t('Retirer le niveau {niveau}', { niveau: dimLabel(d) })} title={t('Retirer ce niveau')} className="oz-stats-chip-x"
                  style={{
                    width: 20, height: 20, border: 'none', borderRadius: 'var(--r-pill)', background: 'transparent', color: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0,
                  }}>
                  <Icon name="x" size={10} stroke={2.6} />
                </button>
              )}
            </span>
            {i < drag.order.length - 1 && <span aria-hidden style={{ color: 'var(--ink-400)', fontSize: 'var(--t-h4)', fontWeight: 700, lineHeight: 1 }}>›</span>}
          </Fragment>
        );
      })}
      {drag.ghost}
      {free.length > 0 && (
        <button type="button" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu((m) => (m ? null : r)); }}
          className="oz-stats-dashed" style={DASHED} title={t('Ajouter un niveau de regroupement')}>
          <Icon name="plus" size={12} stroke={2.6} />{t('Niveau')}
        </button>
      )}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginLeft: 'auto' }}>
        {legend.map((x) => (
          <span key={x.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-600)' }}>
            <span style={{ width: 16, height: 4, borderRadius: 2, background: x.color }} />{x.label}
          </span>
        ))}
      </span>
      {menu && (
        <StatsPopover r={menu} width={220} onClose={() => setMenu(null)}>
          <div style={POP_HEAD}>{t('Ajouter un niveau')}</div>
          <div style={{ padding: '4px 6px 6px' }}>
            {free.map((d) => (
              <button key={d.k} type="button" onClick={() => { onChange([...axes, d.k as K]); setMenu(null); }} className="oz-stats-level-pick"
                style={{
                  display: 'block', width: '100%', height: 34, padding: '0 10px', border: 'none', borderRadius: 'var(--r-xs)',
                  background: 'transparent', textAlign: 'left', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)', cursor: 'pointer',
                }}>
                {dimLabel(d)}
              </button>
            ))}
          </div>
        </StatsPopover>
      )}
    </div>
  );
}
